import moment from "moment";
import Axios from "./Axios";
import APIVariables, { buildPrescriptionReviewPath } from "./api-variables";

/**
 * Map a review outcome option from GET /settings/review-outcome-options/.
 * When the API omits `id`, the array index is used as `review_outcome_id` on POST.
 */
export function mapReviewOutcomeOption(option, index = 0) {
  if (!option || !option.name) return null;
  const hasApiId = option.id != null && option.id !== "";
  return {
    id: hasApiId ? option.id : index,
    name: option.name,
    active: option.active !== false,
    isOther: Boolean(option.is_other),
  };
}

/** GET /settings/review-outcome-options/ — returns active options only. */
export async function fetchReviewOutcomeOptions() {
  const res = await Axios.get(APIVariables.REVIEW_OUTCOME_OPTIONS);
  const list = Array.isArray(res.data) ? res.data : res.data?.results ?? [];
  return list
    .map((option, index) => mapReviewOutcomeOption(option, index))
    .filter((option) => option?.active);
}

export async function submitPrescriptionReview(prescriptionId, payload) {
  await Axios.post(buildPrescriptionReviewPath(prescriptionId), payload);
}

export function parsePrescriptionReviewError(error) {
  const data = error?.response?.data;
  if (typeof data?.review === "string" && data.review.trim()) {
    return data.review;
  }
  if (typeof data?.detail === "string" && data.detail.trim()) {
    return data.detail;
  }
  return "An error occurred while reviewing the prescription.";
}

/** True when API exposes `reviewed_date` and it has not been set yet. */
export function prescriptionNeedsReview(source) {
  if (!source) return false;
  const prescription = source.prescription ?? source;
  if (
    !prescription ||
    !Object.prototype.hasOwnProperty.call(prescription, "reviewed_date")
  ) {
    return false;
  }
  return prescription.reviewed_date == null || prescription.reviewed_date === "";
}

export function buildReviewPayload({
  reviewOutcomeId,
  reviewOutcomeOther,
  selectedOption,
}) {
  const payload = { review_outcome_id: reviewOutcomeId };
  if (selectedOption?.isOther) {
    payload.review_outcome_other = reviewOutcomeOther?.trim() ?? "";
  }
  return payload;
}

const REVIEW_ELIGIBLE_STATUSES = [
  "Submitted",
  "Overdue",
  "Expiring",
  "Status Update",
];

/** True when the prescription has been reviewed (reviewed_date is set). */
export function hasPrescriptionReviewSummary(source) {
  if (!source) return false;
  const prescription = source.prescription ?? source;
  return (
    prescription?.reviewed_date != null && prescription.reviewed_date !== ""
  );
}

export function formatPrescriptionReviewedDate(iso) {
  if (!iso) return "—";
  const m = moment(iso);
  return m.isValid() ? m.format("DD-MMM-YYYY").toUpperCase() : "—";
}

/** Resolves reviewer display name from prescription review fields. */
export function resolveReviewedByLabel(source) {
  const prescription = source?.prescription ?? source;
  if (!prescription) return "—";

  const reviewedBy = prescription.reviewed_by;
  const candidates = [
    prescription.reviewed_by_name,
    prescription.reviewed_by_display,
    typeof reviewedBy === "string" ? reviewedBy : null,
    reviewedBy?.full_name,
    reviewedBy?.name,
    reviewedBy?.first_name && reviewedBy?.last_name
      ? `${reviewedBy.first_name} ${reviewedBy.last_name}`.trim()
      : null,
  ];

  for (const candidate of candidates) {
    if (candidate != null && String(candidate).trim()) {
      return String(candidate).trim();
    }
  }

  return "—";
}

/**
 * Review outcome text for the PAF summary box.
 * When `review_outcome_other` is set, the outcome name is shown as a bold label.
 */
export function getReviewOutcomeDisplay(source) {
  const prescription = source?.prescription ?? source;
  const outcome = prescription?.review_outcome;
  const other = prescription?.review_outcome_other?.trim();

  if (!outcome && !other) return null;

  if (other) {
    return {
      label: outcome || "Other",
      detail: other,
      isOther: true,
    };
  }

  return {
    label: null,
    detail: outcome,
    isOther: false,
  };
}

/** Whether the Review action should be offered for this prescription. */
export function canShowPrescriptionReviewAction({
  canReviewPaf,
  prescription,
  isFormApproved,
}) {
  if (!canReviewPaf || isFormApproved !== null) return false;
  if (!prescriptionNeedsReview(prescription)) return false;
  const status = prescription?.status_name ?? prescription?.status;
  return REVIEW_ELIGIBLE_STATUSES.includes(status);
}
